import { useState, useEffect, useRef } from "react";

/**
 * Custom hook for audio scrubber functionality
 * Manages time tracking, duration, and seeking behavior
 *
 * @param {Object} audioRef - React ref to audio element
 * @param {Object} currentTrack - Current track object with mp3_url
 * @returns {Object} - Scrubber state and handlers
 */
export function useAudioScrubber(audioRef, currentTrack) {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragTime, setDragTime] = useState(0);
  const progressBarRef = useRef(null);
  const prevTrackUrl = useRef(null);

  // Reset time when track changes, but keep duration until new metadata loads
  useEffect(() => {
    if (prevTrackUrl.current !== currentTrack?.mp3_url) {
      prevTrackUrl.current = currentTrack?.mp3_url;
      setCurrentTime(0);
    }
  }, [currentTrack?.mp3_url]);

  // Audio time and duration updates
  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    const updateTime = () => {
      if (!isDragging && isFinite(audio.currentTime)) {
        setCurrentTime(audio.currentTime);
      }
    };

    const updateDuration = () => {
      if (isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("durationchange", updateDuration);

    // Immediately update on mount/audio change
    updateTime();
    updateDuration();

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("durationchange", updateDuration);
    };
  }, [audioRef, isDragging]);

  // Seek to position based on click/drag position
  const seekToPosition = (clientX) => {
    if (!progressBarRef.current) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * duration;

    if (isDragging) {
      setDragTime(newTime);
      setCurrentTime(newTime);
    } else {
      const audio = audioRef?.current;
      if (audio) {
        audio.currentTime = newTime;
      }
    }
  };

  // Mouse down handler - start drag
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragTime(currentTime);
    seekToPosition(e.clientX);
  };

  // Mouse move/up handlers - manage drag
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      seekToPosition(e.clientX);
    };

    const handleMouseUp = () => {
      const audio = audioRef?.current;
      if (audio) {
        audio.currentTime = dragTime;
      }
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, duration, dragTime, audioRef]);

  // Click handler - seek without drag
  const handleSeek = (e) => {
    if (!isDragging) {
      seekToPosition(e.clientX);
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const displayTime = isDragging ? dragTime : currentTime;
  const progressPercent = duration > 0 ? (displayTime / duration) * 100 : 0;

  return {
    // State
    currentTime,
    duration,
    displayTime,
    progressPercent,
    isDragging,

    // Refs
    progressBarRef,

    // Handlers
    handleMouseDown,
    handleSeek,

    // Utils
    formatTime,
  };
}
